/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable quotes */
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { formatActDate, formatCourtDate } from "@/Utils/litigationDates.mjs";

import {
  calculateAge,
  formatCalendarDate,
  formatDateForPanelDisplay,
  currencyFormat,
} from "../../../Utils/helper";

export const exportToCSV = (template, reportData) => {
  // Build headers based on selected template fields
  const headers = [];
  const fieldMap = [];

  if (template?.client_first_name) {
    headers.push("First Name");
    fieldMap.push((row) => row?.for_client?.first_name || "");
  }

  if (template?.client_last_name) {
    headers.push("Last Name");
    fieldMap.push((row) => row?.for_client?.last_name || "");
  }

  if (template?.client_birthday) {
    headers.push("Birthday");
    fieldMap.push((row) => formatCalendarDate(row?.for_client?.birthday));
  }

  if (template?.client_age) {
    headers.push("Age");
    fieldMap.push((row) => calculateAge(row?.for_client?.birthday));
  }

  if (template?.case_type_category) {
    headers.push("Case Type Category");
    fieldMap.push((row) => row?.case_type_category?.name || "");
  }

  if (template?.case_type) {
    headers.push("Case Type");
    fieldMap.push((row) => row?.case_type?.name || "");
  }

  if (template?.incident_date) {
    headers.push("Incident Date");
    fieldMap.push((row) => formatCalendarDate(row?.incident_date));
  }

  if (template?.intake_date) {
    headers.push("Intake Date");
    fieldMap.push((row) => formatCalendarDate(row?.intake_date));
  }

  if (template?.retained_date) {
    headers.push("Retained Date");
    fieldMap.push((row) => formatCalendarDate(row?.retained));
  }

  if (template?.acceptance_date) {
    headers.push("Acceptance Date");
    fieldMap.push((row) => formatCalendarDate(row?.accepted_date));
  }

  if (template?.closed_date) {
    headers.push("Closed Date");
    fieldMap.push((row) => formatCalendarDate(row?.date_closed));
  }

  if (template?.referred_date) {
    headers.push("Referred Date");
    fieldMap.push((row) => formatCalendarDate(row?.case_referred));
  }

  if (template?.rejected_date) {
    headers.push("Rejected Date");
    fieldMap.push((row) => formatCalendarDate(row?.case_rejected));
  }

  if (template?.source_type) {
    headers.push("Source Type");
    fieldMap.push((row) => row?.source_type || "");
  }

  if (template?.source_name) {
    headers.push("Source Name");
    fieldMap.push((row) => row?.source_name || "");
  }

  if (template?.secondary_source_type) {
    headers.push("Secondary Source Type");
    fieldMap.push((row) => row?.secondary_source_type || "");
  }

  if (template?.secondary_source_name) {
    headers.push("Secondary Source Name");
    fieldMap.push((row) => row?.secondary_source_name || "");
  }

  if (template?.third_source_type) {
    headers.push("Third Source Type");
    fieldMap.push((row) => row?.third_source_type || "");
  }

  if (template?.third_source_name) {
    headers.push("Third Source Name");
    fieldMap.push((row) => row?.third_source_name || "");
  }

  if (template?.firm_user_intake) {
    headers.push("Firm User (Intake)");
    fieldMap.push((row) => getUserName(row?.intake_date_user));
  }

  if (template?.firm_user_retained) {
    headers.push("Firm User (Retained)");
    fieldMap.push((row) => getUserName(row?.retained_date_user));
  }

  if (template?.firm_user_accepted) {
    headers.push("Firm User (Accepted)");
    fieldMap.push((row) => getUserName(row?.accepted_date_user));
  }

  if (template?.firm_user_rejected) {
    headers.push("Firm User (Rejected)");
    fieldMap.push((row) => getUserName(row?.case_rejected_user));
  }

  if (template?.firm_user_referred) {
    headers.push("Firm User (Referred)");
    fieldMap.push((row) => getUserName(row?.case_referred_user));
  }

  // Build CSV content
  let csvContent = headers.join(",") + "\n";

  reportData.forEach((row) => {
    const values = fieldMap.map((fn) => {
      const value = fn(row);
      // Escape commas and quotes in CSV
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvContent += values.join(",") + "\n";
  });

  // Download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${template?.template_name || "report"}_${getTimestamp()}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ============================================
// EXCELJS WRITER
// ============================================

const writeWorkbookAndDownload = async (headers, rows, filenameBase) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Report");

  sheet.columns = headers.map((h) => ({ header: h, width: 20 }));
  sheet.getRow(1).font = { bold: true };

  rows.forEach((rowData) => {
    sheet.addRow(rowData);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${filenameBase}_${getTimestamp()}.xlsx`);
};

export const exportToXLSX = async (template, reportData) => {
  const headers = [];
  if (template?.client_first_name) headers.push("First Name");
  if (template?.client_last_name) headers.push("Last Name");
  if (template?.client_birthday) headers.push("Birthday");
  if (template?.client_age) headers.push("Age");
  if (template?.case_type_category) headers.push("Case Type Category");
  if (template?.case_type) headers.push("Case Type");
  if (template?.incident_date) headers.push("Incident Date");
  if (template?.intake_date) headers.push("Intake Date");
  if (template?.retained_date) headers.push("Retained Date");
  if (template?.acceptance_date) headers.push("Acceptance Date");
  if (template?.closed_date) headers.push("Closed Date");
  if (template?.referred_date) headers.push("Referred Date");
  if (template?.rejected_date) headers.push("Rejected Date");
  if (template?.source_type) headers.push("Source Type");
  if (template?.source_name) headers.push("Source Name");
  if (template?.secondary_source_type) headers.push("Secondary Source Type");
  if (template?.secondary_source_name) headers.push("Secondary Source Name");
  if (template?.third_source_type) headers.push("Third Source Type");
  if (template?.third_source_name) headers.push("Third Source Name");
  if (template?.firm_user_intake) headers.push("Firm User (Intake)");
  if (template?.firm_user_retained) headers.push("Firm User (Retained)");
  if (template?.firm_user_accepted) headers.push("Firm User (Accepted)");
  if (template?.firm_user_rejected) headers.push("Firm User (Rejected)");
  if (template?.firm_user_referred) headers.push("Firm User (Referred)");

  const rows = reportData.map((row) => {
    const rowData = [];
    if (template?.client_first_name) rowData.push(row?.for_client?.first_name || "");
    if (template?.client_last_name) rowData.push(row?.for_client?.last_name || "");
    if (template?.client_birthday) rowData.push(formatCalendarDate(row?.for_client?.birthday));
    if (template?.client_age) rowData.push(calculateAge(row?.for_client?.birthday));
    if (template?.case_type_category) rowData.push(row?.case_type_category?.name || "");
    if (template?.case_type) rowData.push(row?.case_type?.name || "");
    if (template?.incident_date) rowData.push(formatCalendarDate(row?.incident_date));
    if (template?.intake_date) rowData.push(formatCalendarDate(row?.intake_date));
    if (template?.retained_date) rowData.push(formatCalendarDate(row?.retained));
    if (template?.acceptance_date) rowData.push(formatCalendarDate(row?.accepted_date));
    if (template?.closed_date) rowData.push(formatCalendarDate(row?.date_closed));
    if (template?.referred_date) rowData.push(formatCalendarDate(row?.case_referred));
    if (template?.rejected_date) rowData.push(formatCalendarDate(row?.case_rejected));
    if (template?.source_type) rowData.push(row?.source_type || "");
    if (template?.source_name) rowData.push(row?.source_name || "");
    if (template?.secondary_source_type) rowData.push(row?.secondary_source_type || "");
    if (template?.secondary_source_name) rowData.push(row?.secondary_source_name || "");
    if (template?.third_source_type) rowData.push(row?.third_source_type || "");
    if (template?.third_source_name) rowData.push(row?.third_source_name || "");
    if (template?.firm_user_intake) rowData.push(getUserName(row?.intake_date_user));
    if (template?.firm_user_retained) rowData.push(getUserName(row?.retained_date_user));
    if (template?.firm_user_accepted) rowData.push(getUserName(row?.accepted_date_user));
    if (template?.firm_user_rejected) rowData.push(getUserName(row?.case_rejected_user));
    if (template?.firm_user_referred) rowData.push(getUserName(row?.case_referred_user));
    return rowData;
  });

  await writeWorkbookAndDownload(headers, rows, template?.template_name || "report");
};

export const exportToPDF = (template, reportData) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.text(template?.template_name || "Firm Report", 14, 15);

  if (template?.date_range_start || template?.date_range_end) {
    doc.setFontSize(10);
    const dateRange = `Period: ${formatDateForPanelDisplay(template?.date_range_start)} - ${formatDateForPanelDisplay(template?.date_range_end)}`;
    doc.text(dateRange, 14, 22);
  }

  const headers = [];
  if (template?.client_first_name) headers.push("First Name");
  if (template?.client_last_name) headers.push("Last Name");
  if (template?.client_birthday) headers.push("Birthday");
  if (template?.client_age) headers.push("Age");
  if (template?.case_type_category) headers.push("Case Type Cat.");
  if (template?.case_type) headers.push("Case Type");
  if (template?.incident_date) headers.push("Incident");
  if (template?.intake_date) headers.push("Intake");
  if (template?.retained_date) headers.push("Retained");
  if (template?.acceptance_date) headers.push("Accepted");
  if (template?.closed_date) headers.push("Closed");
  if (template?.referred_date) headers.push("Referred");
  if (template?.rejected_date) headers.push("Rejected");
  if (template?.source_type) headers.push("Source Type");
  if (template?.source_name) headers.push("Source Name");
  if (template?.secondary_source_type) headers.push("2nd Source Type");
  if (template?.secondary_source_name) headers.push("2nd Source Name");
  if (template?.third_source_type) headers.push("3rd Source Type");
  if (template?.third_source_name) headers.push("3rd Source Name");
  if (template?.firm_user_intake) headers.push("User (Intake)");
  if (template?.firm_user_retained) headers.push("User (Retained)");
  if (template?.firm_user_accepted) headers.push("User (Accepted)");
  if (template?.firm_user_rejected) headers.push("User (Rejected)");
  if (template?.firm_user_referred) headers.push("User (Referred)");

  // Build data rows
  const tableData = reportData.map((row) => {
    const rowData = [];
    if (template?.client_first_name) rowData.push(row?.for_client?.first_name || "");
    if (template?.client_last_name) rowData.push(row?.for_client?.last_name || "");
    if (template?.client_birthday) rowData.push(formatCalendarDate(row?.for_client?.birthday));
    if (template?.client_age) rowData.push(calculateAge(row?.for_client?.birthday));
    if (template?.case_type_category) rowData.push(row?.case_type_category?.name || "");
    if (template?.case_type) rowData.push(row?.case_type?.name || "");
    if (template?.incident_date) rowData.push(formatCalendarDate(row?.incident_date));
    if (template?.intake_date) rowData.push(formatCalendarDate(row?.intake_date));
    if (template?.retained_date) rowData.push(formatCalendarDate(row?.retained));
    if (template?.acceptance_date) rowData.push(formatCalendarDate(row?.accepted_date));
    if (template?.closed_date) rowData.push(formatCalendarDate(row?.date_closed));
    if (template?.referred_date) rowData.push(formatCalendarDate(row?.case_referred));
    if (template?.rejected_date) rowData.push(formatCalendarDate(row?.case_rejected));
    if (template?.source_type) rowData.push(row?.source_type || "");
    if (template?.source_name) rowData.push(row?.source_name || "");
    if (template?.secondary_source_type) rowData.push(row?.secondary_source_type || "");
    if (template?.secondary_source_name) rowData.push(row?.secondary_source_name || "");
    if (template?.third_source_type) rowData.push(row?.third_source_type || "");
    if (template?.third_source_name) rowData.push(row?.third_source_name || "");
    if (template?.firm_user_intake) rowData.push(getUserName(row?.intake_date_user));
    if (template?.firm_user_retained) rowData.push(getUserName(row?.retained_date_user));
    if (template?.firm_user_accepted) rowData.push(getUserName(row?.accepted_date_user));
    if (template?.firm_user_rejected) rowData.push(getUserName(row?.case_rejected_user));
    if (template?.firm_user_referred) rowData.push(getUserName(row?.case_referred_user));

    return rowData;
  });

  autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: template?.date_range_start || template?.date_range_end ? 28 : 20,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 10 },
  });

  doc.save(`${template?.template_name || "report"}_${getTimestamp()}.pdf`);
};

// ============================================
// CASE MANAGEMENT EXPORT FUNCTIONS
// ============================================

export const exportCaseMgmtToCSV = (template, reportData) => {
  const headers = ["First Name", "Last Name", "Case Type"];
  const fieldMap = [
    (row) => row?.for_client?.first_name || "",
    (row) => row?.for_client?.last_name || "",
    (row) => row?.case_type?.name || "",
  ];

  if (template?.screen_by_stage) {
    headers.push("Case Stage");
    fieldMap.push((row) => {
      const stages = row?.auto_case_stage;
      return stages?.length > 0 ? stages[stages.length - 1]?.name || "" : "";
    });
  }

  if (template?.screen_by_status) {
    headers.push("Case Status");
    fieldMap.push((row) => {
      const statuses = row?.auto_case_status;
      return statuses?.map((s) => s?.name).join(", ") || "";
    });
  }

  if (template?.has_issues) {
    headers.push("Has Issues");
    fieldMap.push((row) => (row?.has_issues ? "Yes" : "No"));
  }

  if (template?.is_flagged) {
    headers.push("Is Flagged");
    fieldMap.push((row) => (row?.is_flagged ? "Yes" : "No"));
  }

  let csvContent = headers.join(",") + "\n";

  reportData.forEach((row) => {
    const values = fieldMap.map((fn) => {
      const value = fn(row);
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvContent += values.join(",") + "\n";
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${template?.template_name || "case_mgmt_report"}_${getTimestamp()}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportCaseMgmtToXLSX = async (template, reportData) => {
  const headers = ["First Name", "Last Name", "Case Type"];
  if (template?.screen_by_stage) headers.push("Case Stage");
  if (template?.screen_by_status) headers.push("Case Status");
  if (template?.has_issues) headers.push("Has Issues");
  if (template?.is_flagged) headers.push("Is Flagged");

  const rows = reportData.map((row) => {
    const rowData = [
      row?.for_client?.first_name || "",
      row?.for_client?.last_name || "",
      row?.case_type?.name || "",
    ];

    if (template?.screen_by_stage) {
      const stages = row?.auto_case_stage;
      rowData.push(stages?.length > 0 ? stages[stages.length - 1]?.name || "" : "");
    }
    if (template?.screen_by_status) {
      const statuses = row?.auto_case_status;
      rowData.push(statuses?.map((s) => s?.name).join(", ") || "");
    }
    if (template?.has_issues) rowData.push(row?.has_issues ? "Yes" : "No");
    if (template?.is_flagged) rowData.push(row?.is_flagged ? "Yes" : "No");

    return rowData;
  });

  await writeWorkbookAndDownload(headers, rows, template?.template_name || "case_mgmt_report");
};

export const exportCaseMgmtToPDF = (template, reportData) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.text(template?.template_name || "Case Management Report", 14, 15);

  if (template?.date_range_start || template?.date_range_end) {
    doc.setFontSize(10);
    const dateRange = `Period: ${formatDateForPanelDisplay(template?.date_range_start)} - ${formatDateForPanelDisplay(template?.date_range_end)}`;
    doc.text(dateRange, 14, 22);
  }

  const headers = ["First Name", "Last Name", "Case Type"];
  if (template?.screen_by_stage) headers.push("Case Stage");
  if (template?.screen_by_status) headers.push("Case Status");
  if (template?.has_issues) headers.push("Has Issues");
  if (template?.is_flagged) headers.push("Is Flagged");

  const tableData = reportData.map((row) => {
    const rowData = [
      row?.for_client?.first_name || "",
      row?.for_client?.last_name || "",
      row?.case_type?.name || "",
    ];

    if (template?.screen_by_stage) {
      const stages = row?.auto_case_stage;
      rowData.push(stages?.length > 0 ? stages[stages.length - 1]?.name || "" : "");
    }
    if (template?.screen_by_status) {
      const statuses = row?.auto_case_status;
      rowData.push(statuses?.map((s) => s?.name).join(", ") || "");
    }
    if (template?.has_issues) rowData.push(row?.has_issues ? "Yes" : "No");
    if (template?.is_flagged) rowData.push(row?.is_flagged ? "Yes" : "No");

    return rowData;
  });

  autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: template?.date_range_start || template?.date_range_end ? 28 : 20,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 10 },
  });

  doc.save(`${template?.template_name || "case_mgmt_report"}_${getTimestamp()}.pdf`);
};

// ============================================
// LITIGATION EXPORT FUNCTIONS
// ============================================

export const exportLitigationToCSV = (template, reportData) => {
  const headers = ["First Name", "Last Name", "Case Type"];
  const fieldMap = [
    (row) => row?.case?.for_client?.first_name || "",
    (row) => row?.case?.for_client?.last_name || "",
    (row) => row?.case?.case_type?.name || "",
  ];

  if (template?.screen_by_stage) {
    headers.push("Case Stage");
    fieldMap.push((row) => {
      const stages = row?.case?.auto_case_stage;
      return stages?.length > 0 ? stages[stages.length - 1]?.name || "" : "";
    });
  }

  if (template?.screen_by_status) {
    headers.push("Case Status");
    fieldMap.push((row) => {
      const statuses = row?.case?.auto_case_status;
      return statuses?.map((s) => s?.name).join(", ") || "";
    });
  }

  if (template?.has_issues) {
    headers.push("Has Issues");
    fieldMap.push((row) => (row?.case?.has_issues ? "Yes" : "No"));
  }

  if (template?.is_flagged) {
    headers.push("Is Flagged");
    fieldMap.push((row) => (row?.case?.is_flagged ? "Yes" : "No"));
  }

  if (template?.court_name) {
    headers.push("Court Name");
    fieldMap.push((row) => row?.litigation?.court_name || "");
  }

  if (template?.filing_date) {
    headers.push("Filing Date");
    fieldMap.push((row) => formatCourtDate(row?.litigation?.filing_date));
  }

  if (template?.trial_date) {
    headers.push("Trial Date");
    fieldMap.push((row) => {
      const trialActs = row?.litigation_acts?.filter(
        (act) => act?.event_type_id?.litigation_event_type === "Trial"
      );
      if (!trialActs?.length) return "";
      return trialActs
        .map((act) => {
          const start = act?.start_date ? formatActDate(act, "start_date") : "";
          const end = act?.end_date ? formatActDate(act, "end_date") : "";
          return [start, end].filter(Boolean).join(" - ");
        })
        .join("; ");
    });
  }

  let csvContent = headers.join(",") + "\n";

  reportData.forEach((row) => {
    const values = fieldMap.map((fn) => {
      const value = fn(row);
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvContent += values.join(",") + "\n";
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${template?.template_name || "litigation_report"}_${getTimestamp()}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportLitigationToXLSX = async (template, reportData) => {
  const headers = ["First Name", "Last Name", "Case Type"];
  if (template?.screen_by_stage) headers.push("Case Stage");
  if (template?.screen_by_status) headers.push("Case Status");
  if (template?.has_issues) headers.push("Has Issues");
  if (template?.is_flagged) headers.push("Is Flagged");
  if (template?.court_name) headers.push("Court Name");
  if (template?.filing_date) headers.push("Filing Date");
  if (template?.trial_date) headers.push("Trial Date");

  const rows = reportData.map((row) => {
    const rowData = [
      row?.case?.for_client?.first_name || "",
      row?.case?.for_client?.last_name || "",
      row?.case?.case_type?.name || "",
    ];

    if (template?.screen_by_stage) {
      const stages = row?.case?.auto_case_stage;
      rowData.push(stages?.length > 0 ? stages[stages.length - 1]?.name || "" : "");
    }
    if (template?.screen_by_status) {
      const statuses = row?.case?.auto_case_status;
      rowData.push(statuses?.map((s) => s?.name).join(", ") || "");
    }
    if (template?.has_issues) rowData.push(row?.case?.has_issues ? "Yes" : "No");
    if (template?.is_flagged) rowData.push(row?.case?.is_flagged ? "Yes" : "No");
    if (template?.court_name) rowData.push(row?.litigation?.court_name || "");
    if (template?.filing_date) rowData.push(formatCourtDate(row?.litigation?.filing_date));
    if (template?.trial_date) {
      const trialActs = row?.litigation_acts?.filter(
        (act) => act?.event_type_id?.litigation_event_type === "Trial"
      );
      if (!trialActs?.length) {
        rowData.push("");
      } else {
        rowData.push(
          trialActs
            .map((act) => {
              const start = act?.start_date ? formatActDate(act, "start_date") : "";
              const end = act?.end_date ? formatActDate(act, "end_date") : "";
              return [start, end].filter(Boolean).join(" - ");
            })
            .join("; ")
        );
      }
    }

    return rowData;
  });

  await writeWorkbookAndDownload(headers, rows, template?.template_name || "litigation_report");
};

export const exportLitigationToPDF = (template, reportData) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.text(template?.template_name || "Litigation Report", 14, 15);

  if (template?.date_range_start || template?.date_range_end) {
    doc.setFontSize(10);
    const dateRange = `Period: ${formatDateForPanelDisplay(template?.date_range_start)} - ${formatDateForPanelDisplay(template?.date_range_end)}`;
    doc.text(dateRange, 14, 22);
  }

  const headers = ["First Name", "Last Name", "Case Type"];
  if (template?.screen_by_stage) headers.push("Case Stage");
  if (template?.screen_by_status) headers.push("Case Status");
  if (template?.has_issues) headers.push("Has Issues");
  if (template?.is_flagged) headers.push("Is Flagged");
  if (template?.court_name) headers.push("Court Name");
  if (template?.filing_date) headers.push("Filing Date");
  if (template?.trial_date) headers.push("Trial Date");

  const tableData = reportData.map((row) => {
    const rowData = [
      row?.case?.for_client?.first_name || "",
      row?.case?.for_client?.last_name || "",
      row?.case?.case_type?.name || "",
    ];

    if (template?.screen_by_stage) {
      const stages = row?.case?.auto_case_stage;
      rowData.push(stages?.length > 0 ? stages[stages.length - 1]?.name || "" : "");
    }
    if (template?.screen_by_status) {
      const statuses = row?.case?.auto_case_status;
      rowData.push(statuses?.map((s) => s?.name).join(", ") || "");
    }
    if (template?.has_issues) rowData.push(row?.case?.has_issues ? "Yes" : "No");
    if (template?.is_flagged) rowData.push(row?.case?.is_flagged ? "Yes" : "No");
    if (template?.court_name) rowData.push(row?.litigation?.court_name || "");
    if (template?.filing_date) rowData.push(formatCourtDate(row?.litigation?.filing_date));
    if (template?.trial_date) {
      const trialActs = row?.litigation_acts?.filter(
        (act) => act?.event_type_id?.litigation_event_type === "Trial"
      );
      if (!trialActs?.length) {
        rowData.push("");
      } else {
        rowData.push(
          trialActs
            .map((act) => {
              const start = act?.start_date ? formatActDate(act, "start_date") : "";
              const end = act?.end_date ? formatActDate(act, "end_date") : "";
              return [start, end].filter(Boolean).join(" - ");
            })
            .join("; ")
        );
      }
    }

    return rowData;
  });

  autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: template?.date_range_start || template?.date_range_end ? 28 : 20,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 10 },
  });

  doc.save(`${template?.template_name || "litigation_report"}_${getTimestamp()}.pdf`);
};

// ============================================
// SETTLEMENT EXPORT FUNCTIONS
// ============================================

export const exportSettlementToCSV = (template, reportData) => {
  const headers = ["First Name", "Last Name", "Case Type"];
  const fieldMap = [
    (row) => row?.case?.for_client?.first_name || "",
    (row) => row?.case?.for_client?.last_name || "",
    (row) => row?.case?.case_type?.name || "",
  ];

  if (template?.settlement) {
    headers.push("Settlement");
    fieldMap.push((row) => row?.settlement || "");
  }

  if (template?.settled) {
    headers.push("Settled");
    fieldMap.push((row) => (row?.settled ? "Yes" : "No"));
  }

  if (template?.settlement_check_received) {
    headers.push("Settlement Check Received");
    fieldMap.push((row) => {
      const checks = row?.trust_checks;
      if (!checks?.length) return "";
      return checks.map((c) => formatDateForPanelDisplay(c?.cheque_date)).join(", ");
    });
  }

  if (template?.balance_remaining_in_trust) {
    headers.push("Balance Remaining In Trust");
    fieldMap.push((row) => currencyFormat(row?.trust_balance) || "");
  }

  let csvContent = headers.join(",") + "\n";

  reportData.forEach((row) => {
    const values = fieldMap.map((fn) => {
      const value = fn(row);
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvContent += values.join(",") + "\n";
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${template?.template_name || "settlement_report"}_${getTimestamp()}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportSettlementToXLSX = async (template, reportData) => {
  const headers = ["First Name", "Last Name", "Case Type"];
  if (template?.settlement) headers.push("Settlement");
  if (template?.settled) headers.push("Settled");
  if (template?.settlement_check_received) headers.push("Settlement Check Received");
  if (template?.balance_remaining_in_trust) headers.push("Balance Remaining In Trust");

  const rows = reportData.map((row) => {
    const rowData = [
      row?.case?.for_client?.first_name || "",
      row?.case?.for_client?.last_name || "",
      row?.case?.case_type?.name || "",
    ];

    if (template?.settlement) rowData.push(row?.settlement || "");
    if (template?.settled) rowData.push(row?.settled ? "Yes" : "No");
    if (template?.settlement_check_received) {
      const checks = row?.trust_checks;
      if (!checks?.length) {
        rowData.push("");
      } else {
        rowData.push(checks.map((c) => formatDateForPanelDisplay(c?.cheque_date)).join(", "));
      }
    }
    if (template?.balance_remaining_in_trust)
      rowData.push(currencyFormat(row?.trust_balance) || "");

    return rowData;
  });

  await writeWorkbookAndDownload(headers, rows, template?.template_name || "settlement_report");
};

export const exportSettlementToPDF = (template, reportData) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.text(template?.template_name || "Settlement Report", 14, 15);

  if (template?.date_range_start || template?.date_range_end) {
    doc.setFontSize(10);
    const dateRange = `Period: ${formatDateForPanelDisplay(template?.date_range_start)} - ${formatDateForPanelDisplay(template?.date_range_end)}`;
    doc.text(dateRange, 14, 22);
  }

  const headers = ["First Name", "Last Name", "Case Type"];
  if (template?.settlement) headers.push("Settlement");
  if (template?.settled) headers.push("Settled");
  if (template?.settlement_check_received) headers.push("Check Received");
  if (template?.balance_remaining_in_trust) headers.push("Trust Balance");

  const tableData = reportData.map((row) => {
    const rowData = [
      row?.case?.for_client?.first_name || "",
      row?.case?.for_client?.last_name || "",
      row?.case?.case_type?.name || "",
    ];

    if (template?.settlement) rowData.push(row?.settlement || "");
    if (template?.settled) rowData.push(row?.settled ? "Yes" : "No");
    if (template?.settlement_check_received) {
      const checks = row?.trust_checks;
      if (!checks?.length) {
        rowData.push("");
      } else {
        rowData.push(checks.map((c) => formatDateForPanelDisplay(c?.cheque_date)).join(", "));
      }
    }
    if (template?.balance_remaining_in_trust)
      rowData.push(currencyFormat(row?.trust_balance) || "");

    return rowData;
  });

  autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: template?.date_range_start || template?.date_range_end ? 28 : 20,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 10 },
  });

  doc.save(`${template?.template_name || "settlement_report"}_${getTimestamp()}.pdf`);
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getUserName = (user) => {
  if (!user) return "";
  return `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
};

const getTimestamp = () => {
  const now = new Date();
  return now.toISOString().replace(/[:.]/g, "-").slice(0, -5);
};
